import React, { useEffect, useState } from 'react';
import type { DatenschutzText } from '../../../services/sifa/types';
import { ladeDatenschutzPublic } from '../../../services/sifa/datenschutzService';
import { ladeUnterweisungPublic } from '../../../services/sifa/sifaPublicService';
import { ladeSammelLinkPublic } from '../../../services/sifa/sammellinkService';
import { ladeTestPublic } from '../../../services/sifa/testlinkService';
import { describeError } from '../../../services/errorMessage';
import { datenschutzAnzeige, type TextStueck } from './datenschutzSeiteLogic';
import type { DatenschutzQuelle } from './routenLogic';
import { TeilnahmeRahmen } from './TeilnahmeRahmen';
import { FEHLERKASTEN, HINWEISKASTEN } from './teilnahmeSeiteStil';

type Namen = { kundeName: string; bueroName: string };

type Stand =
  | { status: 'laedt' }
  | { status: 'bereit'; text: DatenschutzText | null; namen: Namen }
  | { status: 'fehler'; meldung: string };

const KEINE_NAMEN: Namen = { kundeName: '', bueroName: '' };

/**
 * Kunden- und Bueroname fuer das Muster — die Datenschutz-RPC liefert sie
 * nur mit einer eigenen Fassung. Best effort: scheitert das, bleiben die
 * Stellen als markierte Luecke stehen, die Seite zeigt trotzdem den Text.
 */
const ladeNamen = async (quelle: DatenschutzQuelle): Promise<Namen> => {
  try {
    const quelleDaten =
      quelle.art === 'sammel'
        ? await ladeSammelLinkPublic(quelle.token)
        : quelle.art === 'test'
          ? await ladeTestPublic(quelle.token)
          : await ladeUnterweisungPublic(quelle.token);
    return quelleDaten ? { kundeName: quelleDaten.kundeName, bueroName: quelleDaten.bueroName } : KEINE_NAMEN;
  } catch {
    return KEINE_NAMEN;
  }
};

/** Text mit markierten Luecken — React-Elemente, nie HTML aus dem gepflegten Text. */
const Stuecke: React.FC<{ stuecke: TextStueck[] }> = ({ stuecke }) => (
  <>
    {stuecke.map((s, i) =>
      s.luecke ? (
        <mark key={i} className="bg-yellow-200 text-slate-900 rounded px-1 box-decoration-clone">
          {s.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{s.text}</React.Fragment>
      ),
    )}
  </>
);

/**
 * Oeffentliche Datenschutzhinweise der Online-Unterweisung
 * #/datenschutz/unterweisung?l=<linkToken> | ?s=<sammelToken> | ?t=<testToken>
 * (docs/sifa-ausbau2-plan.md §0.4, §3.3).
 *
 * Zeigt die geltende Fassung der Firma des Projektbesitzers, sonst das Muster
 * (Stand „Muster"). `{{…}}` werden mit Kunde (Arbeitgeber) und Buero
 * (Auftragsverarbeiter) gefuellt; offene `[…]`-Stellen stehen gelb markiert
 * da — der Teilnehmer sieht ehrlich, was noch nicht ausgefuellt ist.
 * Oeffnet sich in einem neuen Tab neben der laufenden Unterweisung.
 */
export const DatenschutzSeite: React.FC<{ quelle: DatenschutzQuelle | null }> = ({ quelle }) => {
  const [stand, setStand] = useState<Stand>({ status: 'laedt' });
  const art = quelle?.art;
  const token = quelle?.token;

  useEffect(() => {
    if (!art || !token) return undefined;
    let aktiv = true;
    const q: DatenschutzQuelle = { art, token };
    ladeDatenschutzPublic(q)
      .then(async (eigen) => {
        const namen = eigen ? { kundeName: eigen.kundeName, bueroName: eigen.bueroName } : await ladeNamen(q);
        if (aktiv) setStand({ status: 'bereit', text: eigen, namen });
      })
      .catch((e) => {
        if (aktiv) setStand({ status: 'fehler', meldung: describeError(e) });
      });
    return () => {
      aktiv = false;
    };
  }, [art, token]);

  if (!quelle) {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Dieser Link ist unvollständig</h1>
        <div className={HINWEISKASTEN}>
          Bitte öffne die Datenschutzhinweise über den Link in der Unterweisung oder in der Liste der Unterweisungen.
        </div>
      </TeilnahmeRahmen>
    );
  }

  if (stand.status === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">
          Datenschutzhinweise werden geladen…
        </p>
      </TeilnahmeRahmen>
    );
  }

  if (stand.status === 'fehler') {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Die Datenschutzhinweise konnten nicht geladen werden</h1>
        <div className={FEHLERKASTEN}>{stand.meldung}</div>
        <p className="text-slate-600">Bitte prüfe die Internetverbindung und lade die Seite neu.</p>
      </TeilnahmeRahmen>
    );
  }

  const { kundeName, bueroName } = stand.namen;
  const anzeige = datenschutzAnzeige(stand.text, stand.namen);

  return (
    <TeilnahmeRahmen>
      <header className="space-y-1">
        {bueroName && <p className="text-sm font-medium text-slate-500">{bueroName}</p>}
        <h1 className="text-2xl font-bold text-slate-900 leading-tight">{anzeige.titel}</h1>
        {kundeName && <p className="text-slate-600">Arbeitgeber: {kundeName}</p>}
        <p className="text-sm text-slate-500">{anzeige.stand}</p>
      </header>

      {anzeige.offeneLuecken && (
        <div className={HINWEISKASTEN}>
          <mark className="bg-yellow-200 text-slate-900 rounded px-1">Gelb markierte Stellen</mark> sind in dieser
          Fassung noch nicht ausgefüllt.
        </div>
      )}

      <article className="space-y-4 leading-relaxed text-slate-800 break-words">
        {anzeige.bloecke.map((b, i) => {
          if (b.art === 'ueberschrift') {
            return (
              <h2 key={i} className="pt-2 text-lg font-bold text-slate-900 leading-snug">
                <Stuecke stuecke={b.stuecke} />
              </h2>
            );
          }
          if (b.art === 'liste') {
            return (
              <ul key={i} className="list-disc pl-6 space-y-1.5">
                {b.punkte.map((p, j) => (
                  <li key={j}>
                    <Stuecke stuecke={p} />
                  </li>
                ))}
              </ul>
            );
          }
          return (
            <p key={i}>
              {b.zeilen.map((z, j) => (
                <React.Fragment key={j}>
                  {j > 0 && <br />}
                  <Stuecke stuecke={z} />
                </React.Fragment>
              ))}
            </p>
          );
        })}
      </article>

      <p className="pt-3 border-t border-slate-100 text-sm text-slate-500">
        Du kannst dieses Fenster schließen und mit der Unterweisung weitermachen.
      </p>
    </TeilnahmeRahmen>
  );
};
